import { useMemo } from "react";

import { analyzeDraftCompleteness, type CompletenessResult } from "../../lib/draftCompleteness";

interface DraftCompletenessProps {
  markdown: string;
  /** Size of the indicator in pixels */
  size?: number;
}

/**
 * Visual indicator showing draft besluit completeness
 * Shows a circular progress indicator with icon
 */
export function DraftCompleteness({ markdown, size = 48 }: DraftCompletenessProps) {
  const result = useMemo(() => analyzeDraftCompleteness(markdown), [markdown]);

  return (
    <div className="flex items-center gap-3" title={result.message}>
      <CircularProgress result={result} size={size} />
      <div className="text-sm">
        <div className="font-medium text-on-surface-variant">{result.percentage}% compleet</div>
        <div className="text-on-surface-variant/60 text-xs">{result.message}</div>
      </div>
    </div>
  );
}

interface CircularProgressProps {
  result: CompletenessResult;
  size: number;
}

function CircularProgress({ result, size }: CircularProgressProps) {
  const { percentage, level } = result;

  // SVG parameters
  const strokeWidth = size * 0.1;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Colors and icons based on level
  const config = getVisualConfig(level);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={config.bgColor}
          strokeWidth={strokeWidth}
          strokeDasharray={level === 0 ? "4 4" : "none"}
        />
        {/* Progress circle */}
        {percentage > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={config.progressColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
          />
        )}
      </svg>
      {/* Center icon */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ color: config.iconColor }}
      >
        <span className="text-lg">{config.icon}</span>
      </div>
    </div>
  );
}

interface VisualConfig {
  bgColor: string;
  progressColor: string;
  iconColor: string;
  icon: string;
}

function getVisualConfig(level: 0 | 1 | 2 | 3 | 4): VisualConfig {
  switch (level) {
    case 0:
      return {
        bgColor: "oklch(0.87 0 0)", // neutral-variant bg
        progressColor: "oklch(0.70 0 0)", // neutral-variant
        iconColor: "oklch(0.55 0 0)", // on-surface-variant
        icon: "?",
      };
    case 1:
      return {
        bgColor: "oklch(0.90 0.05 25)", // error-container
        progressColor: "oklch(0.55 0.20 25)", // error
        iconColor: "oklch(0.45 0.20 25)", // on-error-container
        icon: "!",
      };
    case 2:
      return {
        bgColor: "oklch(0.90 0.08 65)", // tertiary-container
        progressColor: "oklch(0.65 0.15 65)", // tertiary
        iconColor: "oklch(0.50 0.12 65)", // on-tertiary-container
        icon: "✎",
      };
    case 3:
      return {
        bgColor: "oklch(0.92 0.08 95)", // secondary-container
        progressColor: "oklch(0.65 0.12 95)", // secondary
        iconColor: "oklch(0.50 0.10 95)", // on-secondary-container
        icon: "◐",
      };
    case 4:
      return {
        bgColor: "oklch(0.90 0.08 145)", // primary-container
        progressColor: "oklch(0.55 0.15 145)", // primary
        iconColor: "oklch(0.45 0.15 145)", // on-primary-container
        icon: "✓",
      };
  }
}
